import { cssSelector, textSnippet } from '../capture/selector';

import type {
  ActionEntry,
  ConsoleEntry,
  Environment,
  FeedbackContext,
  NetworkEntry,
} from '../types';

export interface CollectorOptions {
  /** Requests to these URLs are not recorded (the widget's own API calls). */
  ignoreUrl?: (url: string) => boolean;
  /** Clicks inside these nodes are not recorded (the widget itself). */
  ignoreNode?: (node: EventTarget) => boolean;
  limits?: { console?: number; network?: number; actions?: number };
  /** Context from outside the page's own signals: host metadata, Sentry event. */
  extra?: () => Pick<FeedbackContext, 'metadata' | 'sentry'>;
}

export interface Collector {
  install(): void;
  uninstall(): void;
  snapshot(): FeedbackContext;
}

function ring<T>(max: number) {
  const items: T[] = [];
  return {
    push(item: T) {
      items.push(item);
      if (items.length > max) items.shift();
    },
    values: () => items.slice(),
  };
}

const now = () => new Date().toISOString();

function formatArg(arg: unknown): string {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return arg.stack ?? `${arg.name}: ${arg.message}`;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

function formatArgs(args: unknown[]): string {
  const message = args.map(formatArg).join(' ');
  return message.length > 2000 ? `${message.slice(0, 1999)}…` : message;
}

function absoluteUrl(url: string): string {
  try {
    return new URL(url, location.href).href;
  } catch {
    return url;
  }
}

function detectBrowser(ua: string): string {
  const rules: Array<[RegExp, string]> = [
    [/Edg\/([\d.]+)/, 'Edge'],
    [/OPR\/([\d.]+)/, 'Opera'],
    [/Firefox\/([\d.]+)/, 'Firefox'],
    [/Chrome\/([\d.]+)/, 'Chrome'],
    [/Version\/([\d.]+).*Safari/, 'Safari'],
  ];
  for (const [re, name] of rules) {
    const m = ua.match(re);
    if (m) return `${name} ${m[1]?.split('.')[0] ?? ''}`.trim();
  }
  return 'Unknown';
}

function detectOs(ua: string): string {
  if (/Windows NT/.test(ua)) return 'Windows';
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown';
}

export function environment(): Environment {
  const ua = navigator.userAgent;
  return {
    url: location.href,
    title: document.title,
    userAgent: ua,
    browser: detectBrowser(ua),
    os: detectOs(ua),
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    screen: { width: window.screen.width, height: window.screen.height },
    devicePixelRatio: window.devicePixelRatio,
    capturedAt: now(),
  };
}

/**
 * Records console errors, failed network requests and recent user actions from the moment
 * the widget is initialised, so a report can include what happened before it was opened.
 */
export function createCollector(options: CollectorOptions = {}): Collector {
  const consoleLog = ring<ConsoleEntry>(options.limits?.console ?? 30);
  const network = ring<NetworkEntry>(options.limits?.network ?? 30);
  const actions = ring<ActionEntry>(options.limits?.actions ?? 20);
  const ignoreUrl = options.ignoreUrl ?? (() => false);
  const ignoreNode = options.ignoreNode ?? (() => false);
  const restore: Array<() => void> = [];
  let installed = false;

  const recordRequest = (entry: Omit<NetworkEntry, 'time'>) => {
    if (ignoreUrl(entry.url)) return;
    network.push({ ...entry, time: now() });
  };

  function patchConsole() {
    for (const level of ['error', 'warn'] as const) {
      const original = console[level];
      console[level] = (...args: unknown[]) => {
        consoleLog.push({ level, message: formatArgs(args), time: now() });
        original.apply(console, args);
      };
      restore.push(() => {
        console[level] = original;
      });
    }

    const onError = (event: ErrorEvent) => {
      const message = event.error instanceof Error ? formatArg(event.error) : event.message;
      consoleLog.push({ level: 'error', message: `Uncaught ${message}`, time: now() });
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      consoleLog.push({
        level: 'error',
        message: `Unhandled rejection: ${formatArg(event.reason)}`,
        time: now(),
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    restore.push(() => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    });
  }

  function patchFetch() {
    if (typeof window.fetch !== 'function') return;
    const original = window.fetch;
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const method = (
        init?.method ?? (input instanceof Request ? input.method : 'GET')
      ).toUpperCase();
      const url = absoluteUrl(
        typeof input === 'string' ? input : input instanceof URL ? input.href : input.url,
      );
      const start = performance.now();
      try {
        const response = await original.call(window, input, init);
        if (!response.ok) {
          recordRequest({
            method,
            url,
            status: response.status,
            statusText: response.statusText,
            durationMs: Math.round(performance.now() - start),
          });
        }
        return response;
      } catch (error) {
        recordRequest({
          method,
          url,
          status: 0,
          statusText: formatArg(error),
          durationMs: Math.round(performance.now() - start),
        });
        throw error;
      }
    };
    restore.push(() => {
      window.fetch = original;
    });
  }

  function patchXhr() {
    if (typeof XMLHttpRequest === 'undefined') return;
    const proto = XMLHttpRequest.prototype;
    const originalOpen = proto.open;
    const originalSend = proto.send;
    const meta = new WeakMap<XMLHttpRequest, { method: string; url: string }>();

    proto.open = function (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      meta.set(this, { method: method.toUpperCase(), url: absoluteUrl(String(url)) });
      return (originalOpen as (...args: unknown[]) => void).call(this, method, url, ...rest);
    } as typeof proto.open;

    proto.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
      const info = meta.get(this);
      if (info) {
        const start = performance.now();
        this.addEventListener(
          'loadend',
          () => {
            if (this.status === 0 || this.status >= 400) {
              recordRequest({
                ...info,
                status: this.status,
                statusText: this.statusText,
                durationMs: Math.round(performance.now() - start),
              });
            }
          },
          { once: true },
        );
      }
      return originalSend.call(this, body);
    };

    restore.push(() => {
      proto.open = originalOpen;
      proto.send = originalSend;
    });
  }

  function trackActions() {
    const onClick = (event: MouseEvent) => {
      const path = event.composedPath();
      if (path.some((node) => ignoreNode(node))) return;
      const target = path[0];
      if (!(target instanceof Element)) return;
      actions.push({
        type: 'click',
        selector: cssSelector(target),
        text: textSnippet(target),
        time: now(),
      });
    };
    document.addEventListener('click', onClick, true);
    restore.push(() => document.removeEventListener('click', onClick, true));

    let lastUrl = location.href;
    const onNavigate = () => {
      if (location.href === lastUrl) return;
      lastUrl = location.href;
      actions.push({ type: 'navigation', url: lastUrl, time: now() });
    };
    for (const method of ['pushState', 'replaceState'] as const) {
      const original = history[method];
      history[method] = function (this: History, ...args: Parameters<History['pushState']>) {
        const result = original.apply(this, args);
        onNavigate();
        return result;
      };
      restore.push(() => {
        history[method] = original;
      });
    }
    window.addEventListener('popstate', onNavigate);
    window.addEventListener('hashchange', onNavigate);
    restore.push(() => {
      window.removeEventListener('popstate', onNavigate);
      window.removeEventListener('hashchange', onNavigate);
    });
  }

  return {
    install() {
      if (installed) return;
      installed = true;
      patchConsole();
      patchFetch();
      patchXhr();
      trackActions();
    },
    uninstall() {
      while (restore.length) restore.pop()?.();
      installed = false;
    },
    snapshot() {
      return {
        environment: environment(),
        console: consoleLog.values(),
        network: network.values(),
        actions: actions.values(),
        ...options.extra?.(),
      };
    },
  };
}
